import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  Tooltip
} from '@mui/material';
import { 
  CheckCircle, Code, Edit, ExpandMore, Person, Schedule, GitHub, 
  AccessTime, InfoOutlined, Delete, Undo 
} from '@mui/icons-material';
import { AI_PHASES, AI_PHASE_NAMES, AI_TASK_PLANNER, AI_STATUS } from '../../shared/appConstants';
import { COLORS } from '../../shared/colors';
import './AIDeveloperComponents.scss';

const AIDevTabStepper = ({
  currentStep,
  expandedStep,
  setExpandedStep,
  planData,
  codeGenData,
  approvalRepositories,
  loading,
  editingTask,
  editedTasks,
  isEditingAllTasks,
  expandedAccordion,
  setExpandedAccordion,
  handleApprovePlan,
  handleEditTask,
  handleEditAllTasks,
  handleSaveAllTasks,
  handleCancelAllTasks,
  handleSaveTask,
  handleDeleteTask,
  handleRestoreTask,
  deletedTasks,
  setEditedTasks,
  setEditingTask,
  setCurrentStep
}) => {
  const { t } = useTranslation();

  // State conditions for stepper logic
  const isPlanGenerating = planData?.plan === null || planData?.plan?.plan_json === null;
  const isPlanFailed = planData?.plan?.status?.toLowerCase() === AI_STATUS.FAILED && planData?.plan?.plan_json === null;
  const isPlanApproved = planData?.approval?.status === 'APPROVED';
  const hasCodeGenData = codeGenData?.approval?.codeGenerations?.length > 0;
  const hasPullRequests = codeGenData?.approval?.codeGenerations?.[0]?.pullRequests?.length > 0;
  const isCodeGenComplete = hasCodeGenData && hasPullRequests;
  const isCodeGenFailed = codeGenData?.approval?.codeGenerations?.[0]?.status?.toLowerCase() === AI_STATUS.FAILED && (!codeGenData?.approval?.codeGenerations?.[0]?.pullRequests || codeGenData?.approval?.codeGenerations?.[0]?.pullRequests?.length === 0);

  // Determine active step based on workflow progress
  const getActiveStep = () => {
    if (!isPlanApproved) return 0; // Plan generation step
    if (isPlanApproved && !isCodeGenComplete) return 1; // Approval step
    return 2; // Code generation step
  };

  // Update individual task field values
  const updateTaskField = (taskId, field, value) => {
    setEditedTasks(prev => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value
      }
    }));
  };

  // Manage array fields (files, acceptanceCriteria) with add/remove/update operations
  const updateTaskArray = (taskId, arrayField, action, index = null, value = null) => {
    setEditedTasks(prev => {
      const currentArray = prev[taskId][arrayField];
      let newArray;
      
      switch (action) {
        case 'ADD':
          newArray = [...currentArray, ''];
          break;
        case 'REMOVE':
          newArray = currentArray.filter((_, idx) => idx !== index);
          break;
        case 'UPDATE':
          newArray = currentArray.map((item, idx) => idx === index ? value : item);
          break;
        default:
          newArray = currentArray;
      }
      
      return {
        ...prev,
        [taskId]: {
          ...prev[taskId],
          [arrayField]: newArray
        }
      };
    });
  };

  // Map status to appropriate color scheme
  const getStepStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case AI_STATUS.READY:
      case AI_STATUS.PENDING:
        return COLORS.primary;
      case AI_STATUS.FAILED:
        return COLORS.aiTaskPlanner.failed;
      case AI_STATUS.IN_PROGRESS:
      case AI_STATUS.GENERATING:
        return COLORS.aiTaskPlanner.inProgress;
      case AI_STATUS.APPROVED:
      case AI_STATUS.COMPLETED:
      case AI_STATUS.COMPLETE:
        return COLORS.aiTaskPlanner.approved
      default:
        return COLORS.primary;
    }
  };

  const handleCancelEdit = (taskId) => {
    setEditingTask(null);
    setEditedTasks(prev => {
      const newState = { ...prev };
      delete newState[taskId];
      return newState;
    });
  };

  // Task editing handlers using common update functions
  const handleTaskFieldChange = (taskId, field, value) => updateTaskField(taskId, field, value);
  const handleAddFile = (taskId) => updateTaskArray(taskId, 'files', 'ADD');
  const handleRemoveFile = (taskId, fileIndex) => updateTaskArray(taskId, 'files', 'REMOVE', fileIndex);
  const handleFileChange = (taskId, fileIndex, value) => updateTaskArray(taskId, 'files', 'UPDATE', fileIndex, value);
  const handleAddCriteria = (taskId) => updateTaskArray(taskId, 'acceptanceCriteria', 'ADD');
  const handleRemoveCriteria = (taskId, criteriaIndex) => updateTaskArray(taskId, 'acceptanceCriteria', 'REMOVE', criteriaIndex);
  const handleCriteriaChange = (taskId, criteriaIndex, value) => updateTaskArray(taskId, 'acceptanceCriteria', 'UPDATE', criteriaIndex, value);

  return (
    <Box className="stepper-container">
      {/* AI Development workflow stepper with three sequential phases */}
      <Stepper activeStep={getActiveStep()} orientation="vertical">
        {/* Step 1: Plan Generation - Always available, shows plan creation and task management */}
        <Step completed={getActiveStep() > 0}>
          <StepLabel 
            onClick={() => setExpandedStep(expandedStep === 0 ? -1 : 0)}
          >
            <Box className="step-header">
              <Box className="step-title">
                {AI_PHASE_NAMES[AI_PHASES.PLANNING]}
                <ExpandMore className={`expand-icon ${expandedStep === 0 ? 'expanded' : ''}`} />
              </Box>
              {/* Dynamic status chip based on plan generation state */}
              {planData && (isPlanGenerating || !isPlanGenerating) && (
                <Chip
                  label={isPlanFailed ? planData.plan.status : (isPlanGenerating ? t('aiTaskPlanner.stepper.planGeneration.status.generating').toUpperCase() : (planData?.plan?.status || t('aiTaskPlanner.stepper.planGeneration.status.generated')))}
                  size="small"
                  className="step-status"
                  sx={{ 
                    bgcolor: getStepStatusColor(isPlanFailed ? AI_STATUS.FAILED : (isPlanGenerating ? t('aiTaskPlanner.stepper.planGeneration.status.generating') : (isPlanApproved ? t('aiTaskPlanner.stepper.planGeneration.status.approved') : (planData?.plan?.status || t('aiTaskPlanner.stepper.planGeneration.status.generated')))))
                  }}
                />
              )}
            </Box>
          </StepLabel>
          {expandedStep === 0 && (
            <Box className="step-content">
              {/* Show loading state while plan is being generated by AI */}
              {isPlanFailed ? (
                <Paper className="plan-gen-failed">
                  <Box>
                    <Typography variant="h5" className="failed-title">
                      {t('aiTaskPlanner.stepper.planGeneration.failed.title')}
                    </Typography>
                    <Typography variant="body1" className="failed-description">
                      {t('aiTaskPlanner.stepper.planGeneration.failed.description')}
                    </Typography>
                  </Box>
                </Paper>
              ) : isPlanGenerating ? (
                <Paper className="generating-paper">
                  <Box>
                    <Typography variant="h5" className="generating-title">
                      <AccessTime className="progress-icon" />
                      {t('aiTaskPlanner.stepper.planGeneration.inProgress.title')}
                    </Typography>
                    <Typography variant="body1" className="generating-description">
                      {t('aiTaskPlanner.stepper.planGeneration.inProgress.description')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" className="generating-caption">
                    {t('aiTaskPlanner.stepper.planGeneration.inProgress.note')}
                  </Typography>
                </Paper>
              ) : (
                <Paper className="plan-overview-paper">
                  <Box className="overview-header">
                    <Typography variant="h6" className="overview-title">
                      {t('aiTaskPlanner.stepper.planGeneration.overview.title')}
                    </Typography>
                    <Box className="overview-chips">
                      <Chip
                        label={`${planData?.plan?.plan_json?.plan?.tasks?.length || 0} ${t('aiTaskPlanner.stepper.planGeneration.overview.tasks')}`}
                        size="small"
                        className="task-count-chip"
                      />
                      {planData?.plan?.plan_json?.plan?.riskAssessment?.overallRisk && (
                        <Chip
                          label={`${t('aiTaskPlanner.stepper.planGeneration.overview.risk')}: ${planData.plan.plan_json.plan.riskAssessment.overallRisk}`}
                          size="small"
                          className="risk-chip"
                        />
                      )}
                    </Box>
                  </Box>
                  {(planData?.plan?.plan_json?.plan?.overview || planData?.description) ? (
                    <Typography variant="body2" color="text.secondary" className="overview-description">
                      {planData?.plan?.plan_json?.plan?.overview || planData?.description}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" className="overview-description-italic">
                      {t('aiTaskPlanner.stepper.planGeneration.overview.noOverview')}
                    </Typography>
                  )}
                  
                  {planData?.plan?.plan_json?.plan?.dependencies && (
                    <Box className="dependencies-section">
                      <Typography variant="subtitle2" className="dependencies-title">
                        {t('aiTaskPlanner.stepper.planGeneration.overview.dependencies.title')}
                      </Typography>
                      <Box className="dependencies-container">
                        {planData.plan.plan_json.plan.dependencies.npm?.length > 0 && (
                          <Box className="dependency-group">
                            <Typography variant="caption" className="dependency-label">
                              {t('aiTaskPlanner.stepper.planGeneration.overview.dependencies.npm')} ({planData.plan.plan_json.plan.dependencies.npm.length})
                            </Typography>
                            <Box className="dependency-chips">
                              {planData.plan.plan_json.plan.dependencies.npm.map((dep, idx) => (
                                <Chip key={idx} label={dep} size="small" className="npm-chip" />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {planData.plan.plan_json.plan.dependencies.external?.length > 0 && (
                          <Box className="dependency-group">
                            <Typography variant="caption" className="dependency-label">
                              {t('aiTaskPlanner.stepper.planGeneration.overview.dependencies.external')} ({planData.plan.plan_json.plan.dependencies.external.length})
                            </Typography>
                            <Box className="dependency-chips">
                              {planData.plan.plan_json.plan.dependencies.external.map((dep, idx) => (
                                <Chip key={idx} label={dep} size="small" className="external-chip" />
                              ))}
                            </Box>
                          </Box>
                        )}
                        {(!planData.plan.plan_json.plan.dependencies.npm?.length && !planData.plan.plan_json.plan.dependencies.external?.length) && (
                          <Typography variant="caption" className="no-dependencies">
                            {t('aiTaskPlanner.stepper.planGeneration.overview.dependencies.none')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  
                  {planData?.plan?.plan_created_by && (
                    <Typography variant="subtitle2" className="generated-by">
                      {t('aiTaskPlanner.stepper.planGeneration.overview.generatedBy')}: {planData.plan.plan_created_by}
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Task management section - only shown when plan is generated */}
              {!isPlanGenerating && (
                <>
                  <Box className="tasks-header">
                    <Typography variant="subtitle1" className="tasks-title">
                      {t('aiTaskPlanner.stepper.planGeneration.tasks.title')}
                    </Typography>
                    {/* Conditional editing controls based on approval status and current edit mode */}
                    {!isEditingAllTasks && !isPlanApproved ? (
                      <Button
                        startIcon={<Edit />}
                        onClick={handleEditAllTasks}
                        variant="outlined"
                        size="small"
                        className="edit-all-button"
                      >
                        {t('aiTaskPlanner.stepper.planGeneration.tasks.editAndDelete')}
                      </Button>
                    ) : isEditingAllTasks ? (
                      <Box className="edit-actions">
                        <Button
                          variant="outlined"
                          onClick={handleCancelAllTasks}
                          size="small"
                          className="cancel-button"
                        >
                          {t('aiTaskPlanner.stepper.planGeneration.tasks.cancel')}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveAllTasks}
                          size="small"
                          className="save-all-button"
                          disabled={loading}
                        >
                          {t('aiTaskPlanner.stepper.planGeneration.tasks.saveAll')}
                        </Button>
                      </Box>
                    ) : null}
                  </Box>
                  
                  {/* Task list with individual accordions for each task */}
                  {planData?.plan?.plan_json?.plan?.tasks?.length > 0 ? planData.plan.plan_json.plan.tasks.map((task, index) => {
                    const isDeleted = deletedTasks.has(task.id);
                    const isEditing = editingTask === task.id || isEditingAllTasks;
                    const editedTask = editedTasks[task.id] || task;
                    const remainingTasks = planData.plan.plan_json.plan.tasks.filter(t => !deletedTasks.has(t.id));
                    const canDelete = remainingTasks.length > 1 && !isPlanApproved;
                    
                    return (
                      <Accordion 
                        key={task.id} 
                        expanded={!isDeleted && (expandedAccordion === AI_TASK_PLANNER.EXPANDED_ALL || expandedAccordion === index || isEditing)}
                        onChange={(event, isExpanded) => {
                          // Prevent accordion toggle when clicking inside text fields or in edit mode
                          if (!event.target.closest('.MuiTextField-root') && expandedAccordion !== AI_TASK_PLANNER.EXPANDED_ALL && !isEditing) {
                            setExpandedAccordion(isExpanded ? index : -1);
                          }
                        }}
                        className={`task-accordion ${isDeleted ? 'deleted' : ''} ${expandedAccordion === AI_TASK_PLANNER.EXPANDED_ALL || expandedAccordion === index ? 'expanded' : ''}`}
                      >
                        <AccordionSummary 
                          expandIcon={isDeleted || isEditing ? null : <ExpandMore />}
                          className="task-summary"
                        >
                          <Box className="task-header">
                            <Box className="task-info">
                              {isEditing && !isDeleted ? (
                                <TextField
                                  value={editedTask.title}
                                  onChange={(e) => handleTaskFieldChange(task.id, 'title', e.target.value)}
                                  size="small"
                                  fullWidth
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAccordion(index);
                                  }}
                                  className="task-title-field"
                                />
                              ) : (
                                <Box className="task-details">
                                  <Typography 
                                    variant="subtitle2" 
                                    className={`task-title ${isDeleted ? 'deleted-task-title' : ''}`}
                                  >
                                    {typeof task.title === 'string' ? task.title : 'N/A'}
                                  </Typography>
                                  {task.type && (
                                    <Chip
                                      label={typeof task.type === 'string' ? task.type : 'N/A'}
                                      size="small"
                                      className="type-chip"
                                    />
                                  )}
                                  {task.estimatedComplexity && (
                                    <Chip
                                      label={typeof task.estimatedComplexity === 'string' ? task.estimatedComplexity : 'N/A'}
                                      size="small"
                                      className={`complexity-${task.estimatedComplexity}`}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                            {/* Individual task edit button - only shown when not in bulk edit mode and plan not approved */}
                            {!isDeleted && !isEditing && !isEditingAllTasks && !isPlanApproved && (
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditTask(task.id); }} className="task-edit-button">
                                <Edit className="edit-icon" />
                              </IconButton>
                            )}
                          </Box>
                          {/* Restore icon for deleted tasks - separate container */}
                          {isDeleted && (
                            <Box className="restore-icon-container">
                              <Tooltip title="Restore Task" placement="top" slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                                <Undo 
                                  onClick={(e) => { e.stopPropagation(); handleRestoreTask(task.id); }}
                                  className="task-restore-icon"
                                />
                              </Tooltip>
                            </Box>
                          )}
                        </AccordionSummary>
                        <AccordionDetails className="task-content">
                          <Box className="task-field">
                            <Typography variant="subtitle2" className="field-title">
                              {t('aiTaskPlanner.stepper.planGeneration.tasks.fields.description')}
                            </Typography>
                            {isEditing ? (
                              <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={10}
                                value={editedTask.description}
                                onChange={(e) => handleTaskFieldChange(task.id, 'description', e.target.value)}
                                onFocus={() => setExpandedAccordion(index)}
                                placeholder={t('aiTaskPlanner.stepper.planGeneration.tasks.placeholders.description')}
                                className="field-description"
                              />
                            ) : task.description ? (
                              <Typography variant="body2" color="text.secondary" className="field-text">
                                {typeof task.description === 'string' ? task.description : 'N/A'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary" className="field-text-italic">
                                {t('aiTaskPlanner.stepper.planGeneration.tasks.fields.noDescription')}
                              </Typography>
                            )}
                          </Box>
                          
                          <Box className="task-field">
                            <Typography variant="subtitle2" className="field-title">
                              {t('aiTaskPlanner.stepper.planGeneration.tasks.fields.files')}
                            </Typography>
                            {isEditing ? (
                              <Box>
                                {editedTask.files.map((file, fileIndex) => (
                                  <Box key={fileIndex} className="file-input-row">
                                    <TextField
                                      fullWidth
                                      value={file}
                                      onChange={(e) => handleFileChange(task.id, fileIndex, e.target.value)}
                                      onFocus={() => setExpandedAccordion(index)}
                                      size="small"
                                      placeholder={t('aiTaskPlanner.stepper.planGeneration.tasks.placeholders.file')}
                                    />
                                    <IconButton size="small" onClick={() => handleRemoveFile(task.id, fileIndex)} className="delete-button">
                                      <Delete className="delete-icon" />
                                    </IconButton>
                                  </Box>
                                ))}
                                <Typography 
                                  variant="body2" 
                                  className="add-more-link"
                                  onClick={() => handleAddFile(task.id)}
                                >
                                  {t('aiTaskPlanner.stepper.planGeneration.tasks.actions.addFiles')}
                                </Typography>
                              </Box>
                            ) : task.files && task.files.length > 0 ? (
                              <List dense className="file-list">
                                {task.files.map((file, fileIndex) => (
                                  <ListItem key={fileIndex} className="file-item">
                                    <ListItemIcon className="file-icon">
                                      <Code className="code-icon" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={typeof file === 'string' ? file : 'N/A'}
                                      primaryTypographyProps={{ className: 'file-path' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary" className="field-text-italic">
                                {t('aiTaskPlanner.stepper.planGeneration.tasks.fields.noFiles')}
                              </Typography>
                            )}
                          </Box>
                          
                          <Box className="task-field">
                            <Typography variant="subtitle2" className="field-title">
                              {t('aiTaskPlanner.stepper.planGeneration.tasks.fields.acceptanceCriteria')}
                            </Typography>
                            {isEditing ? (
                              <Box>
                                {editedTask.acceptanceCriteria.map((criteria, criteriaIndex) => (
                                  <Box key={criteriaIndex} className="criteria-input-row">
                                    <TextField
                                      fullWidth
                                      value={criteria}
                                      onChange={(e) => handleCriteriaChange(task.id, criteriaIndex, e.target.value)}
                                      onFocus={() => setExpandedAccordion(index)}
                                      size="small"
                                      placeholder={t('aiTaskPlanner.stepper.planGeneration.tasks.placeholders.criteria')}
                                    />
                                    <IconButton size="small" onClick={() => handleRemoveCriteria(task.id, criteriaIndex)} className="delete-button">
                                      <Delete className="delete-icon" />
                                    </IconButton>
                                  </Box>
                                ))}
                                <Typography 
                                  variant="body2" 
                                  className="add-more-link"
                                  onClick={() => handleAddCriteria(task.id)}
                                >
                                  {t('aiTaskPlanner.stepper.planGeneration.tasks.actions.addCriteria')}
                                </Typography>
                              </Box>
                            ) : task.acceptanceCriteria && task.acceptanceCriteria.length > 0 ? (
                              <List dense className="criteria-list">
                                {task.acceptanceCriteria.map((criteria, criteriaIndex) => (
                                  <ListItem key={criteriaIndex} className="criteria-item">
                                    <ListItemIcon className="criteria-icon">
                                      <CheckCircle className="check-icon" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={typeof criteria === 'string' ? criteria : 'N/A'}
                                      primaryTypographyProps={{ className: 'criteria-text' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary" className="field-text-italic">
                                {t('aiTaskPlanner.stepper.planGeneration.tasks.fields.noCriteria')}
                              </Typography>
                            )}
                          </Box>
                          
                          {/* Delete button - positioned below acceptance criteria in edit all mode */}
                          {!isDeleted && isEditingAllTasks && !isPlanApproved && canDelete && (
                            <Box className="task-delete-container">
                              <Button 
                                size="small" 
                                variant="contained"
                                startIcon={<Delete />}
                                onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }} 

                                className="task-delete-button"
                              >
                                {t('aiTaskPlanner.stepper.planGeneration.tasks.deleteTask')}
                              </Button>
                            </Box>
                          )}
                          
                          {/* Individual task save/cancel actions - only shown in single task edit mode */}
                          {isEditing && !isEditingAllTasks && (
                            <Box className="task-actions">
                              <Button 
                                variant="outlined" 
                                onClick={() => handleCancelEdit(task.id)}
                                className="cancel-task-button"
                              >
                                {t('aiTaskPlanner.stepper.planGeneration.tasks.actions.cancel')}
                              </Button>
                              <Button 
                                variant="contained" 
                                onClick={() => handleSaveTask(task.id)}
                                className="save-task-button"
                                disabled={loading}
                              >
                                {t('aiTaskPlanner.stepper.planGeneration.tasks.actions.save')}
                              </Button>
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  }) : (
                    <Typography variant="body2" color="text.secondary" className="no-tasks">
                      {t('aiTaskPlanner.stepper.planGeneration.tasks.noTasks')}
                    </Typography>
                  )}
                </>
              )}

              {/* Risk assessment section if available */}
              {planData?.plan?.plan_json?.plan?.riskAssessment?.risks?.length > 0 && (
                <Accordion className="risk-accordion">
                  <AccordionSummary 
                    expandIcon={<ExpandMore />}
                    className="risk-summary"
                  >
                    <Typography variant="subtitle1" className="risk-title">
                      {t('aiTaskPlanner.stepper.planGeneration.riskAssessment.title')}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {planData.plan.plan_json.plan.riskAssessment.risks.map((risk, index) => (
                      <Box key={index} className="risk-item">
                        <Box className="risk-header">
                          <Typography variant="body2" color="text.secondary" className="risk-mitigation">
                            <strong>{t('aiTaskPlanner.stepper.planGeneration.riskAssessment.mitigation')}</strong> {typeof risk.mitigation === 'string' ? risk.mitigation : 'N/A'}
                          </Typography>
                          <Chip
                            label={risk.severity}
                            size="small"
                            className={`severity-${risk.severity}`}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" className="risk-description">
                          <strong>{t('aiTaskPlanner.stepper.planGeneration.riskAssessment.description')}</strong> {typeof risk.description === 'string' ? risk.description : 'N/A'}
                        </Typography>
                        {index < planData.plan.plan_json.plan.riskAssessment.risks.length - 1 && (
                          <Divider className="risk-divider" />
                        )}
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Plan approval button - only shown when plan is generated but not yet approved */}
              {!isPlanApproved && !isPlanGenerating && (
                <Box className="approval-actions">
                  <Button 
                    startIcon={<CheckCircle />} 
                    onClick={handleApprovePlan}
                    disabled={loading}
                    className="approve-button"
                  >
                    {t('aiTaskPlanner.stepper.planGeneration.actions.approve')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Step>
        
        {/* Step 2: Plan Approval - Shows approval details and repository information */}
        <Step completed={getActiveStep() > 1}>
          <StepLabel 
            onClick={() => setExpandedStep(expandedStep === 1 ? -1 : 1)}
          >
            <Box className="step-header">
              <Box className="step-title">
                {AI_PHASE_NAMES[AI_PHASES.APPROVAL]}
                <ExpandMore className={`expand-icon ${expandedStep === 1 ? 'expanded' : ''}`} />
              </Box>
              {/* Show completion status only when plan is approved */}
              {isPlanApproved && (
                <Chip
                  label={t('aiTaskPlanner.stepper.approval.status.completed').toUpperCase()}
                  size="small"
                  className="step-status"
                  sx={{ 
                    bgcolor: getStepStatusColor(t('aiTaskPlanner.stepper.approval.status.completed'))
                  }}
                />
              )}
            </Box>
          </StepLabel>
          {expandedStep === 1 && (
            <Box className="approval-details">
              {/* Show different content based on approval status */}
              {isPlanApproved ? (
                <>
                  {/* Approval metadata - who approved and when */}
                  <Box className="approval-info">
                    {planData.approval.approval_by && (
                      <Box className="approval-item">
                        <Person className="approval-icon" />
                        <Typography variant="body2" className="approval-text">
                          {t('aiTaskPlanner.stepper.approval.info.approvedBy')} {planData.approval.approval_by}
                        </Typography>
                      </Box>
                    )}
                    {planData.approval.approval_at && (
                      <Box className="approval-item">
                        <Schedule className="approval-icon" />
                        <Typography variant="body2" className="approval-text">
                          {t('aiTaskPlanner.stepper.approval.info.approvedAt')} {new Date(planData.approval.approval_at).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  <Divider className="approval-divider" />
                  
                  <Typography variant="body2" className="approval-summary">
                    <strong>{t('aiTaskPlanner.stepper.approval.info.summary')}</strong> {planData?.plan?.plan_json?.plan?.overview || t('aiTaskPlanner.stepper.approval.info.noSummary')}
                  </Typography>
                  
                  {planData.approval.repo_details ? (
                    <Box className="repo-details">
                      <Typography variant="subtitle2" className="repo-name">
                        {t('aiTaskPlanner.stepper.approval.repository.name')} {planData.approval.repo_details?.git_repo_name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" className="repo-url">
                        {t('aiTaskPlanner.stepper.approval.repository.url')} <Typography component="span" className="repo-link" onClick={() => window.open(planData.approval.repo_details?.repo_url, '_blank')}>
                          {planData.approval.repo_details?.repo_url || 'N/A'}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" className="repo-branch">
                        {t('aiTaskPlanner.stepper.approval.repository.branch')} {planData.approval.repo_details?.base_branch || 'N/A'}
                      </Typography>
                    </Box>
                  ) : approvalRepositories.length > 0 ? (
                    <Box>
                      {approvalRepositories.map((repo, index) => (
                        <Box key={index} className="repo-details">
                          <Typography variant="subtitle2" className="repo-name">
                            {t('aiTaskPlanner.stepper.approval.repository.name')} {repo.git_repo_name || 'Repository'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" className="repo-url">
                            {t('aiTaskPlanner.stepper.approval.repository.url')} <Typography component="span" className="repo-link" onClick={() => window.open(repo.repo_url, '_blank')}>
                              {repo.repo_url || 'N/A'}
                            </Typography>
                          </Typography>
                          <Typography variant="body2" color="text.secondary" className="repo-branch">
                            {t('aiTaskPlanner.stepper.approval.repository.branch')} {repo.base_branch || 'N/A'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" className="no-repo">
                      {t('aiTaskPlanner.stepper.approval.info.noRepo')}
                    </Typography>
                  )}
                  
                  <Box className="approval-notice">
                    <Typography variant="body2" className="notice-title">
                      <InfoOutlined className="notice-icon" />
                      {t('aiTaskPlanner.stepper.approval.notice.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" className="notice-text">
                      {t('aiTaskPlanner.stepper.approval.notice.description')}
                    </Typography>
                  </Box>
                  
                  {/* Navigate to code generation step if no PRs exist yet */}
                  {!hasPullRequests && (
                    <Button 
                      startIcon={<Code />} 
                      variant="contained" 
                      onClick={() => {
                        setCurrentStep(2);
                        setExpandedStep(2);
                      }}
                      className="view-code-button"
                    >
                      {t('aiTaskPlanner.stepper.approval.actions.viewCode')}
                    </Button>
                  )}
                </>
              ) : (
                <Box className="not-available">
                  <Typography variant="body2" color="text.secondary" className="not-available-text">
                    {t('aiTaskPlanner.stepper.approval.notAvailable.text')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="not-available-description">
                    {t('aiTaskPlanner.stepper.approval.notAvailable.description')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Step>
        
        {/* Step 3: Code Generation - Shows code generation progress and pull requests */}
        <Step completed={getActiveStep() > 2}>
          <StepLabel 
            onClick={() => setExpandedStep(expandedStep === 2 ? -1 : 2)}
          >
            <Box className="step-header">
              <Box className="step-title">
                {AI_PHASE_NAMES[AI_PHASES.CODE_GENERATION]}
                <ExpandMore className={`expand-icon ${expandedStep === 2 ? 'expanded' : ''}`} />
              </Box>
              {/* Dynamic status based on code generation progress */}
              {isPlanApproved && (
                <Chip
                  label={(!hasCodeGenData && !hasPullRequests) ? t('aiTaskPlanner.stepper.codeGeneration.status.inProgress').toUpperCase() : (codeGenData?.approval?.codeGenerations?.[0]?.status || t('aiTaskPlanner.stepper.codeGeneration.status.complete').toUpperCase())}
                  size="small"
                  className="step-status"
                  sx={{ 
                    bgcolor: getStepStatusColor((!hasCodeGenData && !hasPullRequests) ? t('aiTaskPlanner.stepper.codeGeneration.status.inProgress') : (codeGenData?.approval?.codeGenerations?.[0]?.status || t('aiTaskPlanner.stepper.codeGeneration.status.complete')))
                  }}
                />
              )}
            </Box>
          </StepLabel>
          {expandedStep === 2 && (
            <Box className="code-gen-details">
              {/* Show different states based on approval and code generation progress */}
              {!isPlanApproved ? (
                <Box className="not-available">
                  <Typography variant="body2" color="text.secondary" className="not-available-text">
                    {t('aiTaskPlanner.stepper.codeGeneration.notAvailable.text')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="not-available-description">
                    {t('aiTaskPlanner.stepper.codeGeneration.notAvailable.description')}
                  </Typography>
                </Box>
              ) : !hasPullRequests ? (
                isCodeGenFailed ? (
                  <Paper className="code-gen-failed">
                    <Box>
                      <Typography variant="h5" className="failed-title">
                        {t('aiTaskPlanner.stepper.codeGeneration.failed.title')}
                      </Typography>
                      <Typography variant="body1" className="failed-description">
                        {t('aiTaskPlanner.stepper.codeGeneration.failed.description')}
                      </Typography>
                    </Box>
                  </Paper>
                ) : (
                  <Paper className="code-gen-progress">
                    <Box>
                      <Typography variant="h5" className="progress-title">
                        <AccessTime className="progress-icon" />
                        {t('aiTaskPlanner.stepper.codeGeneration.inProgress.title')}
                      </Typography>
                      <Typography variant="body1" className="progress-description">
                        {t('aiTaskPlanner.stepper.codeGeneration.inProgress.description')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" className="progress-note">
                      {t('aiTaskPlanner.stepper.codeGeneration.inProgress.note')}
                    </Typography>
                  </Paper>
                )
              ) : isCodeGenComplete ? (
                <>
                  <Typography variant="body2" className="code-gen-summary">
                    <strong>{t('aiTaskPlanner.stepper.codeGeneration.summary')}</strong> {planData?.plan?.plan_json?.plan?.overview || t('aiTaskPlanner.stepper.codeGeneration.defaultSummary')}
                  </Typography>
                  
                  {/* List of generated pull requests with clickable links */}
                  {codeGenData.approval?.codeGenerations?.[0]?.pullRequests?.map((pr, index) => (
                    <Box key={pr.id} className="pr-item" onClick={() => window.open(pr.pr_url, '_blank')}>
                      <Box className="pr-info">
                        <GitHub className="pr-icon" />
                        <Box className="pr-details">
                          <Typography variant="caption" color="text.secondary" className="pr-number">
                            {t('aiTaskPlanner.stepper.codeGeneration.pullRequests.number')}{typeof pr.pr_number === 'string' || typeof pr.pr_number === 'number' ? pr.pr_number : 'N/A'}{pr.git_repo_name && ` • ${typeof pr.git_repo_name === 'string' ? pr.git_repo_name : 'N/A'}`}
                          </Typography>
                          <Typography variant="body2" className="pr-title">
                            {typeof pr.title === 'string' ? pr.title : 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      {/* PR status chip with color coding */}
                      {pr.state && (
                        <Chip
                          label={pr.state.toUpperCase()}
                          size="small"
                          className={`pr-status ${
                            pr.state === 'MERGED' || pr.state === 'merged' ? 'merged' : 
                            pr.state === 'OPEN' || pr.state === 'open' ? 'open' : 
                            pr.state === 'CLOSED' || pr.state === 'closed' ? 'closed' : 
                            'default'
                          }`}
                        />
                      )}
                    </Box>
                  ))}
                  
                  {codeGenData.approval?.codeGenerations?.[0]?.pullRequests?.length > 0 && (
                    <Typography variant="caption" className="pr-review-note">
                      {t('aiTaskPlanner.stepper.codeGeneration.pullRequests.reviewNote')} {codeGenData.approval.codeGenerations[0].pullRequests.length === 1 ? t('aiTaskPlanner.stepper.codeGeneration.pullRequests.single') : t('aiTaskPlanner.stepper.codeGeneration.pullRequests.multiple')}
                    </Typography>
                  )}
                </>
              ) : (
                <Box className="not-available">
                  <Typography variant="body2" color="text.secondary" className="not-available-text">
                    {t('aiTaskPlanner.stepper.codeGeneration.notAvailable.planNotApproved')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" className="not-available-description">
                    {t('aiTaskPlanner.stepper.codeGeneration.notAvailable.planNotApprovedDescription')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Step>
      </Stepper>
    </Box>
  );
};

export default AIDevTabStepper;